/**
 * AreaLayout — guard + shell of the resumable onboarding mini-area
 * (PR-ONB-RESUME-1B, locks #5/#6).
 *
 * Guard = Firebase session AND a valid provisional account via GET /area/me
 * (the pure decision matrix lives in lib/areaGuard.ts):
 *   LOGIN       → /area/login
 *   RETRY_AUTH  → one forced token refresh, then login
 *   BIND        → login ok but never bound → /area/login (bind CTA there)
 *   CONVERTED   → informative screen (future redirect to the portal)
 *   DISABLED    → blocked screen
 *   MAINTENANCE → BE flag OFF → back to the legacy wizard entrance
 *
 * Shell: ONLY Dati / Documenti / Verifica identità / Stato richiesta.
 * No operational menu, no chat (lock #6).
 */

import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { resumableAreaEnabled } from '@/lib/env';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { getAreaMe, type AreaMe } from '@/lib/areaApi';
import { resolveAreaGuard, type AreaGuardDecision } from '@/lib/areaGuard';
import { AreaMeContext } from '@/pages/area/areaMeContext';

const NAV_ITEMS = [
  { to: '/area/dati', label: 'Dati' },
  { to: '/area/documenti', label: 'Documenti' },
  { to: '/area/verifica-identita', label: 'Verifica identità' },
  { to: '/area/stato', label: 'Stato richiesta' },
] as const;

type GuardState =
  | { phase: 'loading' }
  | { phase: 'decided'; decision: AreaGuardDecision };

export function AreaLayout(): JSX.Element {
  // Destructured STABLE members (review fix #1): getToken/markBound are
  // useCallback-stable in the provider, so `evaluate` does not depend on the
  // whole session object (whose identity changes with every bind update).
  const { ready, user, getToken, markBound } = useAreaSession();
  const userUid = user?.uid ?? null;
  const navigate = useNavigate();
  const [guard, setGuard] = useState<GuardState>({ phase: 'loading' });

  const evaluate = useCallback(async (): Promise<void> => {
    if (!ready) return;
    if (!userUid) {
      setGuard({ phase: 'decided', decision: { action: 'LOGIN' } });
      return;
    }
    let token = await getToken();
    let me = token ? await getAreaMe(token) : undefined;
    let decision = resolveAreaGuard({ hasSession: true, ...(me && { me }) });
    if (decision.action === 'RETRY_AUTH') {
      // Expired token: one forced refresh, then give up to login.
      token = await getToken(true);
      me = token ? await getAreaMe(token) : undefined;
      decision = resolveAreaGuard({ hasSession: true, ...(me && { me }) });
      if (decision.action === 'RETRY_AUTH') decision = { action: 'LOGIN' };
    }
    if (decision.action === 'ALLOW' && decision.me.request) {
      // markBound is idempotent (sameBind): an unchanged bind keeps the
      // previous object and cannot re-trigger this effect chain.
      markBound({
        requestPublicId: decision.me.request.publicId,
        draftRevision: decision.me.request.draftRevision,
      });
    }
    setGuard({ phase: 'decided', decision });
  }, [ready, userUid, getToken, markBound]);

  useEffect(() => {
    // Re-evaluate ONLY when the firebase session settles or the identity
    // changes (uid) — never on bind/me updates.
    void evaluate();
  }, [evaluate]);

  if (!resumableAreaEnabled) {
    return <Navigate to="/registrazione-intermediario" replace />;
  }
  if (guard.phase === 'loading') {
    return (
      <FocusedPageShell title="Area onboarding" description="Caricamento in corso…">
        <div />
      </FocusedPageShell>
    );
  }

  const decision = guard.decision;
  switch (decision.action) {
    case 'LOGIN':
    case 'BIND':
    // RETRY_AUTH is resolved inside evaluate() (one forced token refresh);
    // if it ever reaches render, falling back to login is the safe path.
    case 'RETRY_AUTH':
      return <Navigate to="/area/login" replace />;
    case 'MAINTENANCE':
      return (
        <FocusedPageShell
          title="Area onboarding non disponibile"
          description="L'area è temporaneamente disattivata. Puoi comunque presentare la richiesta dal modulo di registrazione."
          footer={
            <PillButton
              type="button"
              size="lg"
              className="w-full"
              onClick={() => navigate('/registrazione-intermediario')}
            >
              Vai al modulo di registrazione
            </PillButton>
          }
        >
          <Banner tone="info" title="Manutenzione">
            Riprova più tardi: la tua bozza resta salvata.
          </Banner>
        </FocusedPageShell>
      );
    case 'CONVERTED':
      return (
        <FocusedPageShell
          title="Account già attivato"
          description="La tua richiesta è stata approvata e l'account è operativo."
        >
          <Banner tone="success" title="Accesso al portale">
            Accedi al portale intermediari con le tue credenziali: questa
            area onboarding non è più necessaria.
          </Banner>
        </FocusedPageShell>
      );
    case 'DISABLED':
      return (
        <FocusedPageShell
          title="Account non abilitato"
          description="Questo account onboarding non è attivo."
        >
          <Banner tone="warning" title="Contatta l'assistenza">
            Per maggiori informazioni scrivi all'assistenza Aurino Broker.
          </Banner>
        </FocusedPageShell>
      );
    case 'ERROR':
      return (
        <FocusedPageShell
          title="Area onboarding"
          description="Non siamo riusciti a caricare la tua area."
          footer={
            <PillButton
              type="button"
              size="lg"
              className="w-full"
              onClick={() => {
                setGuard({ phase: 'loading' });
                void evaluate();
              }}
            >
              Riprova
            </PillButton>
          }
        >
          <Banner tone="warning" title="Errore di caricamento">
            Controlla la connessione e riprova.
          </Banner>
        </FocusedPageShell>
      );
    case 'ALLOW':
      return <AreaShell me={decision.me} onRefresh={() => void evaluate()} />;
  }
}

function AreaShell({
  me,
  onRefresh,
}: {
  me: AreaMe;
  onRefresh: () => void;
}): JSX.Element {
  return (
    <AreaMeContext.Provider value={{ me, refresh: onRefresh }}>
      <div className="mx-auto w-full max-w-2xl px-4 pt-6">
        <nav
          aria-label="Sezioni area onboarding"
          className="mb-2 flex flex-wrap gap-2"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'rounded-full px-4 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground',
                ].join(' ')
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <Outlet />
    </AreaMeContext.Provider>
  );
}
